import React, { useState, useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { Nullable } from "@jamesgmarks/utilities";
import { ISubscriptions } from "@llws/lift-entity-interfaces";
import {
  CustomConfigSchema,
  PromotedListingsSchema,
  TCustomConfiguration,
} from "./customConfigMeta";
import { JsonRenderer } from "src/components/pages/Subscriptions/EditorComponents/JsonRenderer";

interface IPromotedListingsConfigurationEditorProps {
  config: Pick<TCustomConfiguration, "promotedListings">;
  onSave: (updatedConfig: Pick<TCustomConfiguration, "promotedListings">) => void;
  onCancel: () => void;
}

export const PromotedListingsConfigurationEditor: React.FC<IPromotedListingsConfigurationEditorProps> = ({
  config,
  onSave,
  onCancel,
}) => {
  const [daysThreshold, setDaysThreshold] = useState<string>(
    config.promotedListings?.daysActiveThresholdOverride?.toString() ?? "",
  );

  const validationError = useMemo(() => {
    const parsed = daysThreshold.trim() === ""
      ? { daysActiveThresholdOverride: null }
      : { daysActiveThresholdOverride: Number(daysThreshold) };

    const result = PromotedListingsSchema.safeParse(parsed);
    return result.success ? null : "Must be a number or left blank.";
  }, [daysThreshold]);

  const handleSave = () => {
    const finalValue = daysThreshold.trim() === "" ? null : Number(daysThreshold);
    onSave({ promotedListings: { daysActiveThresholdOverride: finalValue } });
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="body1" mt={2}>
          <u>Days Active Threshold Override</u>
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Days Threshold"
          value={daysThreshold}
          onChange={(e) => setDaysThreshold(e.target.value)}
          placeholder="Leave blank for default behavior"
        />
      </Grid>

      {validationError && (
        <Grid item xs={12}>
          <Alert severity="warning">{validationError}</Alert>
        </Grid>
      )}

      <Grid item container spacing={2} justifyContent="center" mt={2}>
        <Grid item>
          <Button variant="contained" color="error" onClick={onCancel}>
            Cancel
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            color="success"
            disabled={!!validationError}
            onClick={handleSave}
          >
            Update
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

interface IPromotedListingsConfigurationAccordionProps {
  isEditingSubscriptionConfiguration: boolean;
  setIsEditingSubscriptionConfiguration: React.Dispatch<React.SetStateAction<boolean>>;
  baseSubscriptionConfiguration: Nullable<TCustomConfiguration>;
  subscriptionConfiguration: Nullable<TCustomConfiguration>;
  setCustomConfiguration: (config: TCustomConfiguration) => void;
  storeSubscription: Nullable<ISubscriptions>;
  hasErrors?: boolean;
}

export const PromotedListingsConfigurationAccordion: React.FC<IPromotedListingsConfigurationAccordionProps> = ({
  isEditingSubscriptionConfiguration,
  setIsEditingSubscriptionConfiguration,
  baseSubscriptionConfiguration,
  subscriptionConfiguration,
  setCustomConfiguration,
  storeSubscription,
}) => {
  const onCopyBaseSubscriptionConfiguration = () => {
    const baseConfig = CustomConfigSchema.safeParse(baseSubscriptionConfiguration);
    if (baseConfig.success) {
      setCustomConfiguration(baseConfig.data);
    }
  };

  const onCopyOriginalSubscriptionConfiguration = () => {
    const config = CustomConfigSchema.safeParse(storeSubscription?.customConfiguration);
    if (config.success) {
      setCustomConfiguration(config.data);
    }
  };

  return (
    <Grid container spacing={1} textAlign="center">
      <Grid item xs={isEditingSubscriptionConfiguration ? 5 : 6}>
        <Typography variant="h6" textAlign="center" mb={2}>
          Base subscription configuration
        </Typography>

        {baseSubscriptionConfiguration?.promotedListings ? (
          <JsonRenderer
            obj={baseSubscriptionConfiguration.promotedListings}
            keyReplacements={{ daysActiveThresholdOverride: "Days Active Threshold" }}
          />
        ) : (
          <Alert icon={false} color="warning" sx={{ justifyContent: "center" }}>
            <i>No promoted listings config in base subscription.</i>
          </Alert>
        )}
      </Grid>

      <Grid item xs={isEditingSubscriptionConfiguration ? 7 : 6}>
        <Typography variant="h6" textAlign="center" mb={2}>
          <b>Subscription configuration to save...</b>
        </Typography>

        {isEditingSubscriptionConfiguration && subscriptionConfiguration ? (
          <PromotedListingsConfigurationEditor
            config={{ promotedListings: subscriptionConfiguration.promotedListings }}
            onSave={(updatedConfig) => {
              const config = {
                ...subscriptionConfiguration,
                promotedListings: updatedConfig.promotedListings,
              };

              setCustomConfiguration(config);
              setIsEditingSubscriptionConfiguration(false);
            }}
            onCancel={() => setIsEditingSubscriptionConfiguration(false)}
          />
        ) : subscriptionConfiguration?.promotedListings ? (
          <Box>
            <JsonRenderer
              obj={subscriptionConfiguration.promotedListings}
              backgroundColor="#fff7e0"
              keyReplacements={{ daysActiveThresholdOverride: "Days Active Threshold" }}
            />
            <Grid item xs={12} mt={2}>
              <Button
                fullWidth
                variant="contained"
                color="warning"
                onClick={() => setIsEditingSubscriptionConfiguration(true)}
              >
                Edit
              </Button>
            </Grid>
          </Box>
        ) : (
          <Alert icon={false} color="warning" sx={{ justifyContent: "center" }}>
            <i>No promoted listings config in subscription.</i>
          </Alert>
        )}

        <Grid item xs={12} mt={2}>
          <Button
            fullWidth
            variant="contained"
            onClick={onCopyOriginalSubscriptionConfiguration}
            disabled={!storeSubscription?.customConfiguration?.promotedListings}
          >
            Use last-saved subscription configuration
          </Button>
        </Grid>

        <Grid item xs={12} my={2}>
          <Button
            fullWidth
            variant="contained"
            onClick={onCopyBaseSubscriptionConfiguration}
            disabled={!baseSubscriptionConfiguration?.promotedListings}
          >
            Copy base subscription configuration
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};
